import _, { get } from 'lodash';
import { apiClient, handleError } from '../api/ApiClient';
import { showAlert } from '../common/components/alert/AlertActions';
import AlertVariant from '../common/components/alert/AlertVariant';
import RouterPaths from '../constants/RouterPaths';
import { customNavigate } from '../common/hooks/useCustomNavigate';
import {
  CLEAR_DEALER_DETAILS,
  CLEAR_DEALER_VALIDATION_ERRORS_SUCCESS,
  EDIT_DEALER_FAILURE,
  FETCH_DEALER_CHANGES,
  FETCH_DEALER_CHANGES_FAILURE,
  FETCH_DEALER_CHANGES_SUCCESS,
  FETCH_DEALER_NOTES_FAILURE,
  FETCH_DEALER_NOTES_SUCCESS,
  GET_DEALER,
  GET_DEALER_FAILURE,
  GET_DEALER_SUCCESS,
  SEARCH_BLACKLIST_DEALERS_SUCCESS,
  SEARCH_DEALER,
  SEARCH_DEALER_FAILURE,
  SEARCH_DEALER_SUCCESS,
} from './DealersReducer';
import { DealerStatus } from '../constants/DealerStatus';
import { prepareSearchData, prepareSortOption, SORT_DESC } from '../helpers/prepareSearchData';
import { FormRegExp } from '../constants/FormRegExp';
import { poundsToPences } from '../common/helpers/poundsToPences';
import {
  clearDealerEmptyValues,
  clearDealersEmptyValues,
} from '../common/helpers/clearDealerEmptyValues';

const DEALERS_ENDPOINT = '/dealer';

export const getDealer = (id) => (dispatch) =>
  apiClient
    .get(`${DEALERS_ENDPOINT}/${id}`)
    .then((res) => {
      dispatch({ type: GET_DEALER });
      if (res.status === 200) {
        const data = res?.data?.data;
        if (data.mainProfile) {
          data.mainProfile = clearDealerEmptyValues(data.mainProfile);
        }
        if (data?.organization?.mainLocation) {
          data.organization.mainLocation = clearDealerEmptyValues(
            data.organization.mainLocation,
          );
        }
        dispatch({ type: GET_DEALER_SUCCESS, payload: { data } });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
      dispatch({ type: GET_DEALER_FAILURE });
    });

export const deleteDealer = (id) => (dispatch) =>
  apiClient
    .delete(`${DEALERS_ENDPOINT}/${id}`)
    .then((res) => {
      if (res.status === 200) {
        dispatch(showAlert('Dealer removed successfully', AlertVariant.SUCCESS));
        setTimeout(() => {
          customNavigate(RouterPaths.DEALERS);
        }, 2000);
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const editDealer = (id, values, statusValue) => (dispatch) => {
  const prepareMainProfile = () => {
    const {
      profileId,
      firstName,
      lastName,
      email,
      mailchimpId,
      status,
      isOwner,
      isBuyer,
      isSeller,
      phoneNumber,
      landlinePhoneNumber,
      position,
      acceptTos,
      acceptNewsletter,
      profileVersion,
      profileSerial,
    } = values;
    return {
      id: profileId,
      firstName,
      lastName,
      email,
      mailchimpId,
      status: statusValue || status,
      isOwner,
      isBuyer,
      isSeller,
      phoneNumber,
      landlinePhoneNumber: landlinePhoneNumber || null,
      position: position.value,
      acceptTos,
      acceptNewsletter,
      version: profileVersion,
      serial: profileSerial,
      adminManagerId:
        get(values, 'accountManager.value') || get(values, 'accountManager.id') || null,
    };
  };
  const prepareOrganization = () => {
    const {
      additionalEmails,
      additionalSalesEmails,
      organizationId,
      vatNumber,
      officialCompanyName,
      tradeName,
      buyerNextGearId,
      sellerNextGearId,
      companyRegistrationNumber,
      role,
      website,
      organizationVersion,
      organizationSerial,
      deletedOn,
      organizationFeesVersion,
      organizationFeesSerial,
      mainLocation,
      address,
      address2,
      city,
      county,
      locations,
      monthlySubscriptionInPence,
      monthlySubscriptionFeeExemption,
      monthlySubscriptionFeeExemptionTo,
      organizationGroup,
      launchInPence,
      launchFeeExemption,
      launchFeeExemptionTo,
      saleForLessThan1000InPence,
      saleForLessThan1000FeeExemption,
      saleForLessThan1000FeeExemptionTo,
      saleBetween1001And5000InPence,
      saleBetween1001And5000FeeExemption,
      saleBetween1001And5000FeeExemptionTo,
      saleBetween5001And10000InPence,
      saleBetween5001And10000FeeExemption,
      saleBetween5001And10000FeeExemptionTo,
      saleBetween10001And15000InPence,
      saleBetween10001And15000FeeExemption,
      saleBetween10001And15000FeeExemptionTo,
      saleForMoreThan15000InPence,
      saleForMoreThan15000FeeExemption,
      saleForMoreThan15000FeeExemptionTo,
      purchaseForLessThan1000InPence,
      purchaseForLessThan1000FeeExemption,
      purchaseForLessThan1000FeeExemptionTo,
      purchaseBetween1001And5000InPence,
      purchaseBetween1001And5000FeeExemption,
      purchaseBetween1001And5000FeeExemptionTo,
      purchaseBetween5001And10000InPence,
      purchaseBetween5001And10000FeeExemption,
      purchaseBetween5001And10000FeeExemptionTo,
      purchaseBetween10001And15000InPence,
      purchaseBetween10001And15000FeeExemption,
      purchaseBetween10001And15000FeeExemptionTo,
      purchaseForMoreThan15000InPence,
      purchaseForMoreThan15000FeeExemption,
      purchaseForMoreThan15000FeeExemptionTo,
      paymentMethod,
    } = values;

    const parseCurrencyToIntIfString = (value) => {
      if (typeof value === 'string') {
        const result = Number(
          value.replace(FormRegExp.INT_WITHOUT_PREFIX_AND_SPECIAL_CHARS, ''),
        );
        if (!Number.isNaN(result)) return poundsToPences(result);
      }
      return poundsToPences(value);
    };

    const locationsData = locations.map((item) => {
      item.address2 = item?.address2?.trim() || null;
      item.postalCodeId =
        item.postalCode.value || item.postalCode.id || item.postalCode.postcode || '';
      return item;
    });
    return {
      id: organizationId,
      accountManagerId:
        get(values, 'accountManager.value') || get(values, 'accountManager.id') || null,
      vatNumber,
      officialCompanyName,
      tradeName: tradeName?.trim() || null,
      buyerNextGearId: buyerNextGearId?.trim() || null,
      sellerNextGearId: sellerNextGearId?.trim() || null,
      companyRegistrationNumber: companyRegistrationNumber || null,
      groupId: get(organizationGroup, 'value'),
      organizationFees: {
        id,
        version: organizationFeesVersion,
        serial: organizationFeesSerial,
        paymentMethod: get(paymentMethod, 'value'),
        monthlySubscriptionInPence: parseCurrencyToIntIfString(monthlySubscriptionInPence),
        monthlySubscriptionFeeExemption: monthlySubscriptionFeeExemption.value === true,
        monthlySubscriptionFeeExemptionTo:
          monthlySubscriptionFeeExemption.value === true
            ? monthlySubscriptionFeeExemptionTo
            : null,
        launchInPence: parseCurrencyToIntIfString(launchInPence),
        launchFeeExemption: launchFeeExemption.value === true,
        launchFeeExemptionTo: launchFeeExemption.value === true ? launchFeeExemptionTo : null,
        saleForLessThan1000InPence: parseCurrencyToIntIfString(saleForLessThan1000InPence),
        saleForLessThan1000FeeExemption: saleForLessThan1000FeeExemption.value === true,
        saleForLessThan1000FeeExemptionTo:
          saleForLessThan1000FeeExemption.value === true
            ? saleForLessThan1000FeeExemptionTo
            : null,
        saleBetween1001And5000InPence: parseCurrencyToIntIfString(
          saleBetween1001And5000InPence,
        ),
        saleBetween1001And5000FeeExemption: saleBetween1001And5000FeeExemption.value === true,
        saleBetween1001And5000FeeExemptionTo:
          saleBetween1001And5000FeeExemption.value === true
            ? saleBetween1001And5000FeeExemptionTo
            : null,
        saleBetween5001And10000InPence: parseCurrencyToIntIfString(
          saleBetween5001And10000InPence,
        ),
        saleBetween5001And10000FeeExemption:
          saleBetween5001And10000FeeExemption.value === true,
        saleBetween5001And10000FeeExemptionTo:
          saleBetween5001And10000FeeExemption.value === true
            ? saleBetween5001And10000FeeExemptionTo
            : null,
        saleBetween10001And15000InPence: parseCurrencyToIntIfString(
          saleBetween10001And15000InPence,
        ),
        saleBetween10001And15000FeeExemption:
          saleBetween10001And15000FeeExemption.value === true,
        saleBetween10001And15000FeeExemptionTo:
          saleBetween10001And15000FeeExemption.value === true
            ? saleBetween10001And15000FeeExemptionTo
            : null,
        saleForMoreThan15000InPence: parseCurrencyToIntIfString(saleForMoreThan15000InPence),
        saleForMoreThan15000FeeExemption: saleForMoreThan15000FeeExemption.value === true,
        saleForMoreThan15000FeeExemptionTo:
          saleForMoreThan15000FeeExemption.value === true
            ? saleForMoreThan15000FeeExemptionTo
            : null,
        purchaseForLessThan1000InPence: parseCurrencyToIntIfString(
          purchaseForLessThan1000InPence,
        ),
        purchaseForLessThan1000FeeExemption:
          purchaseForLessThan1000FeeExemption.value === true,
        purchaseForLessThan1000FeeExemptionTo:
          purchaseForLessThan1000FeeExemption.value === true
            ? purchaseForLessThan1000FeeExemptionTo
            : null,
        purchaseBetween1001And5000InPence: parseCurrencyToIntIfString(
          purchaseBetween1001And5000InPence,
        ),
        purchaseBetween1001And5000FeeExemption:
          purchaseBetween1001And5000FeeExemption.value === true,
        purchaseBetween1001And5000FeeExemptionTo:
          purchaseBetween1001And5000FeeExemption.value === true
            ? purchaseBetween1001And5000FeeExemptionTo
            : null,
        purchaseBetween5001And10000InPence: parseCurrencyToIntIfString(
          purchaseBetween5001And10000InPence,
        ),
        purchaseBetween5001And10000FeeExemption:
          purchaseBetween5001And10000FeeExemption.value === true,
        purchaseBetween5001And10000FeeExemptionTo:
          purchaseBetween5001And10000FeeExemption.value === true
            ? purchaseBetween5001And10000FeeExemptionTo
            : null,
        purchaseBetween10001And15000InPence: parseCurrencyToIntIfString(
          purchaseBetween10001And15000InPence,
        ),
        purchaseBetween10001And15000FeeExemption:
          purchaseBetween10001And15000FeeExemption.value === true,
        purchaseBetween10001And15000FeeExemptionTo:
          purchaseBetween10001And15000FeeExemption.value === true
            ? purchaseBetween10001And15000FeeExemptionTo
            : null,
        purchaseForMoreThan15000InPence: parseCurrencyToIntIfString(
          purchaseForMoreThan15000InPence,
        ),
        purchaseForMoreThan15000FeeExemption:
          purchaseForMoreThan15000FeeExemption.value === true,
        purchaseForMoreThan15000FeeExemptionTo:
          purchaseForMoreThan15000FeeExemption.value === true
            ? purchaseForMoreThan15000FeeExemptionTo
            : null,
      },
      mainLocation: {
        postalCodeId:
          mainLocation.postalCode.value ||
          mainLocation.postalCode.id ||
          mainLocation.postalCode.postcode ||
          '',
        name: mainLocation.name,
        address,
        address2: address2?.trim() || null,
        city,
        county,
        version: mainLocation.version || 0,
      },
      additionalLocations: locationsData,
      additionalEmails: prepareAdditionalEmail(additionalEmails),
      additionalSalesEmails: prepareAdditionalEmail(additionalSalesEmails),
      role: role.value,
      website: website?.trim() || null,
      version: organizationVersion,
      serial: organizationSerial,
      deletedOn,
    };
  };

  const prepareAdditionalEmail = (additionalEmails) => {
    if (_.isEmpty(additionalEmails)) return null;
    return additionalEmails.map((item) => item.email);
  };

  const data = {
    mainProfileForm: prepareMainProfile(),
    organizationForm: prepareOrganization(),
  };

  return apiClient
    .put(`${DEALERS_ENDPOINT}/${id}`, data)
    .then((res) => {
      if (res.status === 200) {
        const isActivation =
          statusValue === DealerStatus.ACTIVE && values?.status === DealerStatus.PENDING;
        const isPromoCodeEmpty = !values?.promoCode || values?.promoCode?.trim()?.length === 0;
        const successMsg =
          isActivation && isPromoCodeEmpty
            ? 'Free monthly subscription trial set for 90 days starting today.'
            : 'Dealer updated successfully';
        dispatch(showAlert(successMsg, AlertVariant.SUCCESS));
        setTimeout(() => {
          customNavigate(RouterPaths.DEALERS);
        }, 2000);
      }
    })
    .catch((err) => {
      dispatch(showAlert(err, AlertVariant.DANGER));

      if (err?.response?.status === 412) {
        return dispatch({ type: EDIT_DEALER_FAILURE, payload: err?.response?.data?.data });
      }
      return dispatch(handleError(err));
    });
};

export const editDealerOrganization =
  (id, { status, version }) =>
  (dispatch, getState) => {
    const organizationId = getState().dealers?.toJS().dealerDetails?.organization?.id;
    return apiClient
      .post(`${DEALERS_ENDPOINT}/organization-change/${id}`, { status, version })
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            showAlert(
              status === DealerStatus.ACTIVE
                ? 'Changes approved successfully'
                : 'Changes rejected successfully',
              AlertVariant.SUCCESS,
            ),
          );
          dispatch(getDealer(organizationId));
        }
      })
      .catch((err) => {
        dispatch(handleError(err));
      });
  };

export const searchDealer = (values) => (dispatch) =>
  apiClient
    .post(`${DEALERS_ENDPOINT}/search`, prepareSearchData(values, prepareSortOption(values)))
    .then((res) => {
      dispatch({ type: SEARCH_DEALER });
      if (res.status === 200) {
        let content = get(res, 'data.data.content', []);
        content = clearDealersEmptyValues(content);

        dispatch({
          type: SEARCH_DEALER_SUCCESS,
          payload: {
            items: content,
            totalItems: get(res, 'data.data.totalElements', 0),
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
      dispatch({ type: SEARCH_DEALER_FAILURE });
    });

export const fetchDealerChanges = (values, organizationId) => (dispatch) =>
  apiClient
    .post(
      `${DEALERS_ENDPOINT}/${organizationId}/changelog`,
      prepareSearchData(values, { createdOn: SORT_DESC }),
    )
    .then((res) => {
      dispatch({ type: FETCH_DEALER_CHANGES });
      if (res.status === 200) {
        dispatch({
          type: FETCH_DEALER_CHANGES_SUCCESS,
          payload: {
            dealerChanges: {
              items: get(res, 'data.data.content', []),
              totalItems: get(res, 'data.data.totalElements', 0),
            },
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
      dispatch({ type: FETCH_DEALER_CHANGES_FAILURE });
    });

export const fetchDealerNotes = (values, organizationId) => (dispatch) =>
  apiClient
    .post(
      `${DEALERS_ENDPOINT}/${organizationId}/notes`,
      prepareSearchData(values, { createdOn: SORT_DESC }),
    )
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: FETCH_DEALER_NOTES_SUCCESS,
          payload: {
            dealerNotes: {
              items: get(res, 'data.data.content', []),
              totalItems: get(res, 'data.data.totalElements', 0),
            },
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
      dispatch({ type: FETCH_DEALER_NOTES_FAILURE });
    });

export const addDealerNote = (values, organizationId, successCallback) => (dispatch) => {
  const data = {
    content: values?.contentNote,
  };
  return apiClient
    .post(`${DEALERS_ENDPOINT}/${organizationId}/add-note`, data)
    .then((res) => {
      if (res.status === 200) {
        dispatch(showAlert('Note added to dealer successfully', AlertVariant.SUCCESS));
        successCallback();
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });
};

export const getDealers = (tradeName, successCallback) => (dispatch) =>
  apiClient
    .post(
      `${DEALERS_ENDPOINT}/search`,
      prepareSearchData({ tradeName }, { serial: SORT_DESC }, true),
    )
    .then((res) => {
      successCallback(res.data);
    })
    .catch((err) => dispatch(handleError(err)));

export const getDealersCities = (city, successCallback) => (dispatch) =>
  apiClient
    .post(`/organization/location/city/search`, prepareSearchData({ city }))
    .then((res) => {
      successCallback(res.data);
    })
    .catch((err) => dispatch(handleError(err)));

export const getOrganizationLocations = (organizationId, successCallback) => (dispatch) =>
  apiClient
    .get(`/organization/${organizationId}/location`)
    .then((res) => {
      successCallback(res.data);
    })
    .catch((err) => dispatch(handleError(err)));

export const searchBlacklistDealers = (values, organizationId) => (dispatch) =>
  apiClient
    .post(
      `${DEALERS_ENDPOINT}/${organizationId}/blacklist`,
      prepareSearchData(values, { serial: SORT_DESC }),
    )
    .then((res) => {
      if (res.status === 200) {
        dispatch({
          type: SEARCH_BLACKLIST_DEALERS_SUCCESS,
          payload: {
            items: get(res, 'data.data.content', []),
            totalItems: get(res, 'data.data.totalElements', 0),
          },
        });
      }
    })
    .catch((err) => {
      dispatch(handleError(err));
    });

export const createBlacklistDealer =
  (organizationId, blockedOrganizationId, successCallback) => (dispatch) =>
    apiClient
      .post(`${DEALERS_ENDPOINT}/${organizationId}/blacklist/${blockedOrganizationId}`, {})
      .then((res) => {
        if (res.status === 200) {
          dispatch(showAlert('Dealer added to blacklist successfully', AlertVariant.SUCCESS));
          successCallback();
        }
      })
      .catch((err) => {
        dispatch(handleError(err));
      });

export const deleteBlacklistDealer =
  (organizationId, blockedOrganizationId, successCallback) => (dispatch) =>
    apiClient
      .delete(`${DEALERS_ENDPOINT}/${organizationId}/blacklist/${blockedOrganizationId}`)
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            showAlert('Dealer removed from blacklist successfully', AlertVariant.SUCCESS),
          );
          successCallback();
        }
      })
      .catch((err) => {
        dispatch(handleError(err));
      });

export const clearDealerFeesValidationErrors = () => (dispatch) => {
  dispatch({ type: CLEAR_DEALER_VALIDATION_ERRORS_SUCCESS });
};

export const clearDealerDetails = () => (dispatch) => {
  dispatch({ type: CLEAR_DEALER_DETAILS });
};
